import {
    formatMetric,
    formatSignedDuration,
    MetricTone,
    toneFromSignedValue,
    toneFromTimeDiff,
} from '../../profile/printer-content/helpers/calc-printer-monthly-stats'
import { PrinterMachineColumn } from '../../../types/api/user'
import { PRINTER_MACHINE_COLUMNS, SHIFT_HEADER_COLORS } from '../constants/shift-grid'
import { VolumeSummaryCell, VolumeSummaryShift } from '../helpers/build-volume-summary'
import { FC } from 'react'
import styled from 'styled-components'

const Wrap = styled.div`
    overflow-x: auto;
    margin-bottom: 24px;
`

const Table = styled.table`
    border-collapse: collapse;
    min-width: 100%;
    font-size: 12px;
`

const ShiftHeader = styled.th<{ $color: string }>`
    background: ${p => p.$color};
    color: #fff;
    font-weight: 600;
    padding: 8px;
    text-align: center;
`

const MachineHeader = styled.th`
    background: #f5f5f5;
    border: 1px solid #d9d9d9;
    padding: 6px 8px;
    white-space: nowrap;
    text-align: center;
    font-weight: 600;
`

const AssistantsHeader = styled(MachineHeader)`
    background: #f9f0ff;
`

const RowLabel = styled.td`
    border: 1px solid #d9d9d9;
    padding: 6px 8px;
    white-space: nowrap;
    background: #fafafa;
    font-weight: 500;
`

const Cell = styled.td<{ $tone?: MetricTone | null }>`
    border: 1px solid #d9d9d9;
    padding: 6px 8px;
    text-align: center;
    white-space: nowrap;
    color: ${p =>
        p.$tone === 'success' ? '#3f8600' : p.$tone === 'danger' ? '#cf1322' : 'inherit'};
`

const METRIC_ROWS = [
    { key: 'fio', label: 'ФИО' },
    { key: 'time', label: 'Время' },
    { key: 'printJobs', label: 'Количество тиражей' },
    { key: 'volume', label: 'Объем' },
    { key: 'savings', label: 'Перерасход/экономия' },
] as const

function renderMetric(cell: VolumeSummaryCell, key: (typeof METRIC_ROWS)[number]['key']) {
    const { stats, username } = cell

    switch (key) {
        case 'time':
            return {
                text:
                    stats.timeDiffMinutes == null
                        ? '—'
                        : formatSignedDuration(stats.timeDiffMinutes),
                tone:
                    stats.timeDiffMinutes == null ? null : toneFromTimeDiff(stats.timeDiffMinutes),
            }
        case 'printJobs':
            return { text: String(stats.printJobsCount), tone: null }
        case 'fio':
            return { text: username, tone: null }
        case 'volume':
            return {
                text: stats.volume == null ? '—' : formatMetric(stats.volume),
                tone: null,
            }
        case 'savings':
            return {
                text: stats.savings == null ? '—' : formatMetric(stats.savings),
                tone: stats.savings == null ? null : toneFromSignedValue(stats.savings),
            }
    }
}

interface ShiftVolumeTableProps {
    shift: VolumeSummaryShift
    title: string
}

const ShiftVolumeTable: FC<ShiftVolumeTableProps> = ({ shift, title }) => {
    const assistantCount = shift.assistants.length
    const machineColSpan = PRINTER_MACHINE_COLUMNS.reduce((sum, column) => {
        return sum + Math.max(1, shift.cells[column]?.length ?? 0)
    }, 0)
    const colSpan = 1 + machineColSpan + assistantCount

    return (
        <Wrap>
            <Table>
                <thead>
                    <tr>
                        <ShiftHeader $color={SHIFT_HEADER_COLORS[shift.shift]} colSpan={colSpan}>
                            {title}
                        </ShiftHeader>
                    </tr>
                    <tr>
                        <MachineHeader />
                        {PRINTER_MACHINE_COLUMNS.map(column => (
                            <MachineHeader
                                key={column}
                                colSpan={Math.max(1, shift.cells[column]?.length ?? 0)}
                            >
                                {column}
                            </MachineHeader>
                        ))}
                        {assistantCount > 0 && (
                            <AssistantsHeader colSpan={assistantCount}>Помощники</AssistantsHeader>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {METRIC_ROWS.map(row => (
                        <tr key={row.key}>
                            <RowLabel>{row.label}</RowLabel>
                            {PRINTER_MACHINE_COLUMNS.map((column: PrinterMachineColumn) => {
                                const columnCells = shift.cells[column] ?? []

                                if (columnCells.length === 0) {
                                    return <Cell key={column} />
                                }

                                return columnCells.map(cell => {
                                    const rendered = renderMetric(cell, row.key)

                                    return (
                                        <Cell key={`${column}-${cell.documentId}`} $tone={rendered.tone}>
                                            {rendered.text}
                                        </Cell>
                                    )
                                })
                            })}
                            {shift.assistants.map(assistant => {
                                const rendered = renderMetric(assistant, row.key)

                                return (
                                    <Cell key={`${assistant.documentId}-${row.key}`} $tone={rendered.tone}>
                                        {rendered.text}
                                    </Cell>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Wrap>
    )
}

export default ShiftVolumeTable
